import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { DoubleLeftOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import axios from 'axios';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRoad } from '@fortawesome/free-solid-svg-icons/faRoad';
import { useDebounceValue, useInterval, useSessionStorage } from "usehooks-ts";
import { axiosErrorHandler, lowerCaseIncludes, showErrorNotification } from '../../../../../swzutils';
import { GoogleMap, AdvancedMarker, MarkerCluster } from '../../../components/googleMap';
import { Cluster } from "@googlemaps/markerclusterer";
import { CENTRE_OF_AUSTRALIA } from "../../../components/googleMap/Model";
import MarkerLargeBuilder from '../markers/MarkerLargeBuilder';
import MarkerSmallBuilder from '../markers/MarkerSmallBuilder';
import MarketplaceConsignmentInfoWindow from '../MarketplaceConsignmentInfoWindow';
import MarketplaceVehicleHireInfoWindow from '../MarketplaceVehicleHireInfoWindow';
import FilterPanel from './FilterPanel';
import { userCanAccessAllTenants, isUserMultiTenant, userHasContractTenancy, userHasPermission } from "../../../../../swzutils/UserPermissionsUtil";
import ConsignmentAssignVehicleModal from "../../consignments/ConsignmentAssignVehicleModal";
import VehicleHireAllocateVehicleModal, { VehicleHireAllocateTab } from "../../vehicleHires/VehicleHireAllocateVehicleModal";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../../store/slice/auth";
import { VehicleTypeCategoryEnum } from "../../vehicleTypes/api/Representation";
import DriverLocationInfoWindow from "../DriverLocationInfoWindow";
import MarketplaceClusterInfoWindow from "../MarketplaceClusterInfoWindow";
import { useAppSelector } from "../../../../../store";
const ZINDEX_DRIVER_LOCATION_MARKER = 10;
const ZINDEX_CONSIGNMENT_MARKER = 20;
const ZINDEX_VEHICLE_HIRE_MARKER = 30;
const ZINDEX_SELECTED_MARKER = 999;
const DriverMap = () => {
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const [sessionIncludeEmployees, setSessionIncludeEmployees] = useSessionStorage("map_include_employees", true);
    const [sessionIncludeContractors, setSessionIncludeContractors] = useSessionStorage("map_include_contractors", false);
    const [sessionIncludeCarriers, setSessionIncludeCarriers] = useSessionStorage("map_include_carriers", false);
    const [sessionIncludeMarketplace, setSessionIncludeMarketplace] = useSessionStorage("map_include_marketplace_jobs", false); // set to true if user has permissions
    const [fetchingEmployees, setFetchingEmployees] = useState(false);
    const [fetchingContractors, setFetchingContractors] = useState(false);
    const [fetchingConsignments, setFetchingConsignments] = useState(false);
    const [fetchingVehicleHires, setFetchingVehicleHires] = useState(false);
    const [includeMarketplaceJobs, setIncludeMarketplaceJobs] = useState(sessionIncludeMarketplace);
    const [boundMapToMarkers, setBoundMapToMarkers] = useState(true);
    const [centerAroundCurrentLocation, setCenterAroundCurrentLocation] = useState(false);
    const [fetchContractorsOnMapCentreChanged, setFetchContractorsOnMapCentreChanged] = useState(true);
    const [showAssignToVehicleModal, setShowAssignToVehicleModal] = useState(false);
    const [showAllocateToVehicleModal, setShowAllocateToVehicleModal] = useState(false);
    const [selectedDriverLocation, setSelectedDriverLocation] = useState(null);
    const [selectedConsignment, setSelectedConsignment] = useState(null);
    const [selectedConsignmentMarker, setSelectedConsignmentMarker] = useState(null);
    const [selectedConsignmentMarkerPosition, setSelectedConsignmentMarkerPosition] = useState(null);
    const [selectedVehicleHire, setSelectedVehicleHire] = useState(null);
    const [selectedVehicleHireMarker, setSelectedVehicleHireMarker] = useState(null);
    const [selectedVehicleHireMarkerPosition, setSelectedVehicleHireMarkerPosition] = useState(null);
    const [selectedMarketplaceClusterMarkers, setSelectedMarketplaceClusterMarkers] = useState([]);
    const [selectedMarketplaceClusterPosition, setSelectedMarketplaceClusterPosition] = useState(null);
    const [employeeDriverLocations, setEmployeeDriverLocations] = useState([]);
    const [contractorDriverLocations, setContractorDriverLocations] = useState([]);
    const [marketplaceConsignmentMarkers, setMarketplaceConsignmentMarkers] = useState([]);
    const [marketplaceVehicleHireMarkers, setMarketplaceVehicleHireMarkers] = useState([]);
    const [searchText, setSearchText] = useState(null);
    const [showTraffic, setShowTraffic] = useState(false);
    const [hideSearch, setHideSearch] = useState(window.innerWidth < 800);
    const [mapCenter, setMapCenter] = useDebounceValue(null, 1500);
    const [vehicleTypeCategories, setVehicleTypeCategories] = useDebounceValue(undefined, 1500);
    useEffect(() => {
        setBoundMapToMarkers(true);
        setFetchContractorsOnMapCentreChanged(true);
        setCenterAroundCurrentLocation(false);
        setMapCenter(null); // Reset map center so it recalibrates to the changed tenant's employee vehicles or current location
        clearSelectedConsignment();
        clearSelectedMarketplaceCluster();
        clearSelectedVehicleHire();
        clearDriverSelection();
        if (sessionIncludeMarketplace && loggedInUser && selectedTenant?.id) {
            if (userCanAccessAllTenants(loggedInUser)
                || (userHasContractTenancy(loggedInUser, selectedTenant)
                    && userHasPermission(loggedInUser, ['admin:consignment:listMarketplace', 'admin:vehicleHires:listMarketplace']))) {
                setSessionIncludeMarketplace(true);
            }
        }
    }, [loggedInUser, selectedTenant?.id]);
    useEffect(() => {
        fetchEmployeeDriverLocations();
    }, [sessionIncludeEmployees, vehicleTypeCategories, selectedTenant?.id]);
    useEffect(() => {
        fetchContractorAndCarrierDriverLocations();
    }, [sessionIncludeContractors, sessionIncludeCarriers, vehicleTypeCategories, selectedTenant?.id]);
    useEffect(() => {
        if (fetchContractorsOnMapCentreChanged) {
            setFetchContractorsOnMapCentreChanged(false);
            fetchContractorAndCarrierDriverLocations();
        }
    }, [mapCenter]);
    useEffect(() => {
        if (selectedTenant?.id) {
            fetchMarketplaceConsignments();
            fetchMarketplaceVehicleHires();
        }
    }, [includeMarketplaceJobs, selectedTenant?.id]);
    useInterval(() => {
        // Clear auto bounds flag on first refresh so the
        // map doesn't keep jumping every time the markers change
        setBoundMapToMarkers(false);
        fetch();
    }, 120000);
    const fetch = () => {
        fetchEmployeeDriverLocations();
        if (mapCenter) {
            fetchContractorAndCarrierDriverLocations();
        }
        const hasListMarketplacePermission = userHasPermission(loggedInUser, ['admin:consignment:listMarketplace']);
        if (includeMarketplaceJobs && hasListMarketplacePermission) {
            fetchMarketplaceConsignments();
        }
        else {
            setMarketplaceConsignmentMarkers([]);
        }
        if (includeMarketplaceJobs && hasListMarketplacePermission) {
            fetchMarketplaceVehicleHires();
        }
        else {
            setMarketplaceVehicleHireMarkers([]);
        }
    };
    const fetchEmployeeDriverLocations = () => {
        if (fetchingEmployees) {
            // If a network request is still in progress then don't fire another one.
            return;
        }
        if (isUserMultiTenant(loggedInUser) && !selectedTenant?.id) {
            // SubTrux admins and Multi Tenant Users must have a tenant selected to see employees of that tenancy
            setEmployeeDriverLocations([]);
            return;
        }
        if (!sessionIncludeEmployees) {
            setEmployeeDriverLocations([]);
            setCenterAroundCurrentLocation(boundMapToMarkers);
            return;
        }
        setFetchingEmployees(true);
        const params = {
            includeEmployees: true,
            includeContractors: false,
            includeCarriers: false,
            excludeForwardDated: true,
            vehicleTypeCategory: vehicleTypeCategories,
            limit: 999
        };
        axios("/admin/v1/drivers/locations", {
            method: "get",
            params: params
        }).then(response => {
            Modal.destroyAll();
            if (response.data.results.length) {
                setEmployeeDriverLocations(response.data.results.map(loc => {
                    return {
                        ...loc,
                        markerRef: React.createRef()
                    };
                }));
            }
            else {
                // If there are no employee vehicles then boundMapToMarkers won't work so attempt to
                // center map based on user location
                setCenterAroundCurrentLocation(boundMapToMarkers);
                setEmployeeDriverLocations([]);
            }
        })
            .catch(axiosErrorHandler)
            .finally(() => setFetchingEmployees(false));
    };
    const fetchContractorAndCarrierDriverLocations = () => {
        if (fetchingContractors) {
            // If a network request is still in progress then don't fire another one.
            return;
        }
        if (!mapCenter || (!sessionIncludeContractors && !sessionIncludeCarriers)) {
            setContractorDriverLocations([]);
            return;
        }
        setFetchingContractors(true);
        const params = {
            includeEmployees: false,
            includeContractors: sessionIncludeContractors,
            includeCarriers: sessionIncludeCarriers,
            excludeForwardDated: true,
            vehicleTypeCategory: vehicleTypeCategories,
            nearbyToLat: mapCenter.lat,
            nearbyToLng: mapCenter.lng,
            limit: 999
        };
        axios("/admin/v1/drivers/locations", {
            method: "get",
            params: params
        }).then(response => {
            Modal.destroyAll();
            setContractorDriverLocations(response.data.results.map(loc => {
                return {
                    ...loc,
                    markerRef: React.createRef()
                };
            }));
        })
            .catch(axiosErrorHandler)
            .finally(() => setFetchingContractors(false));
    };
    useEffect(() => {
        fetchEmployeeDriverLocations();
    }, [sessionIncludeEmployees]);
    const fetchMarketplaceConsignments = () => {
        if (fetchingConsignments) {
            return;
        }
        if (!includeMarketplaceJobs) {
            setMarketplaceConsignmentMarkers([]);
            return;
        }
        setFetchingConsignments(true);
        axios.get("/admin/v1/marketplace/consignments?limit=200")
            .then(response => {
            const marketplaceConsignmentMarkers = response.data.results
                .filter(c => c.pickupAddress.latitude != null && c.pickupAddress.longitude != null)
                .map((consignment) => {
                const zIndex = selectedConsignment === consignment ? ZINDEX_SELECTED_MARKER : ZINDEX_CONSIGNMENT_MARKER;
                return {
                    key: "c" + consignment.id,
                    onClick: onConsignmentMarkerClick,
                    imageUri: "/assets/images/map-marker-pickup-round-dark.svg",
                    data: consignment,
                    title: consignment.consignmentRef,
                    zIndex: zIndex,
                    position: {
                        lat: consignment.pickupAddress.latitude,
                        lng: consignment.pickupAddress.longitude
                    }
                };
            });
            setMarketplaceConsignmentMarkers(marketplaceConsignmentMarkers);
        })
            .catch(axiosErrorHandler)
            .finally(() => setFetchingConsignments(false));
    };
    const fetchMarketplaceVehicleHires = () => {
        if (fetchingVehicleHires) {
            return;
        }
        if (!includeMarketplaceJobs) {
            setMarketplaceVehicleHireMarkers([]);
            return;
        }
        setFetchingVehicleHires(true);
        axios.get("/admin/v1/marketplace/vehicle-hires?limit=200")
            .then(response => {
            const marketplaceVehicleHireMarkers = response.data.results
                .filter(vh => {
                const loc = vh.locations.find(location => location.type === 'STARTING');
                return loc && loc.address && loc.address.latitude && loc.address.longitude;
            })
                .map((vehicleHire) => {
                const startingLocation = vehicleHire.locations.find((location) => location.type === 'STARTING');
                const zIndex = selectedVehicleHire === vehicleHire ? ZINDEX_SELECTED_MARKER : ZINDEX_VEHICLE_HIRE_MARKER;
                return {
                    key: "v" + vehicleHire.id,
                    onClick: onVehicleHireMarkerClick,
                    imageUri: "/assets/images/map-marker-blank-round.svg",
                    data: vehicleHire,
                    title: vehicleHire.ref,
                    zIndex: zIndex,
                    position: {
                        lat: startingLocation.address.latitude,
                        lng: startingLocation.address.longitude
                    }
                };
            });
            setMarketplaceVehicleHireMarkers(marketplaceVehicleHireMarkers);
        })
            .catch(axiosErrorHandler)
            .finally(() => setFetchingVehicleHires(false));
    };
    const onConsignmentMarkerClick = (props, element) => {
        setSelectedMarketplaceClusterMarkers([]);
        setSelectedMarketplaceClusterPosition(null);
        setSelectedConsignment(props.data);
        setSelectedConsignmentMarker(element);
    };
    const onClusteredConsignmentMarkerClick = (marker) => {
        setSelectedMarketplaceClusterMarkers([]);
        setSelectedConsignment(marker.data);
        setSelectedConsignmentMarkerPosition(selectedMarketplaceClusterPosition);
        setSelectedConsignmentMarker(null);
    };
    const clearSelectedConsignment = () => {
        setSelectedConsignment(null);
        setSelectedConsignmentMarker(null);
        setSelectedConsignmentMarkerPosition(null);
    };
    const onVehicleHireMarkerClick = (props, element) => {
        setSelectedMarketplaceClusterMarkers([]);
        setSelectedMarketplaceClusterPosition(null);
        setSelectedVehicleHire(props.data);
        setSelectedVehicleHireMarker(element);
    };
    const onClusteredVehicleHireMarkerClick = (marker) => {
        setSelectedMarketplaceClusterMarkers([]);
        setSelectedVehicleHire(marker.data);
        setSelectedVehicleHireMarkerPosition(selectedMarketplaceClusterPosition);
        setSelectedVehicleHireMarker(null);
    };
    const clearSelectedVehicleHire = () => {
        setSelectedVehicleHire(null);
        setSelectedVehicleHireMarker(null);
        setSelectedVehicleHireMarkerPosition(null);
    };
    const hideAssignToVehicleModal = () => {
        setShowAssignToVehicleModal(false);
    };
    const hideAllocateToVehicleModal = () => {
        setShowAllocateToVehicleModal(false);
    };
    const onSearchPanelDriverClicked = (driverLocation) => {
        setSelectedDriverLocation(driverLocation);
    };
    const onClusterClicked = (cluster, markers) => {
        if (markers.length > 0) {
            clearSelectedConsignment();
            clearSelectedVehicleHire();
            setSelectedMarketplaceClusterMarkers(markers);
            setSelectedMarketplaceClusterPosition({
                lat: cluster.position.lat(),
                lng: cluster.position.lng()
            });
        }
    };
    const clearSelectedMarketplaceCluster = () => {
        setSelectedMarketplaceClusterMarkers([]);
    };
    const onMapClicked = () => {
        clearDriverSelection();
        clearSelectedConsignment();
        clearSelectedVehicleHire();
        clearSelectedMarketplaceCluster();
    };
    const clearDriverSelection = () => {
        setSelectedDriverLocation(null);
    };
    const onSearch = (searchText) => {
        setBoundMapToMarkers(false);
        if (!searchText || searchText.trim().length === 0) {
            setSearchText(null);
            return;
        }
        setSearchText(searchText.toLowerCase());
    };
    const toggleOpenCloseSearchPanel = () => {
        setHideSearch(!hideSearch);
    };
    const onCurrentLocationFailed = ( /*error*/) => {
        showErrorNotification("SubTrux is unable to determine your current location and as a result cannot centre the map based on this.  Please change your browser permissions to allow SubTrux to determine your current location or alternatively manually zoom the map to a desired location to see more details.");
    };
    const onFilterVehicleCategoriesChanged = (value) => {
        setBoundMapToMarkers(false);
        if (Array.isArray(value) && value.length > 0) {
            setVehicleTypeCategories(value);
        }
        else {
            setVehicleTypeCategories(undefined);
        }
    };
    let filteredEmployeeDriverLocations;
    if (searchText) {
        filteredEmployeeDriverLocations = employeeDriverLocations.filter((location) => {
            if (location.driverSummary && lowerCaseIncludes(location.driverSummary.firstName, searchText)) {
                return true;
            }
            if (location.driverSummary && lowerCaseIncludes(location.driverSummary.lastName, searchText)) {
                return true;
            }
            return !!(location.vehicle && lowerCaseIncludes(location.vehicle.registration, searchText));
        });
    }
    else {
        filteredEmployeeDriverLocations = [...employeeDriverLocations, ...contractorDriverLocations];
    }
    // @ts-ignore
    const marketplaceMarkers = [...marketplaceVehicleHireMarkers, ...marketplaceConsignmentMarkers];
    const driverLocationMarkers = filteredEmployeeDriverLocations.map((location) => {
        const zIndex = selectedDriverLocation === location ? ZINDEX_SELECTED_MARKER : ZINDEX_DRIVER_LOCATION_MARKER;
        return (_jsx(AdvancedMarker, { onClick: setSelectedDriverLocation, imageUri: location.vehicle && location.vehicle.registration
                ? MarkerLargeBuilder.buildMarker(location, loggedInUser, selectedTenant?.id)
                : MarkerSmallBuilder.buildMarker(location), data: location, title: location.vehicle && location.vehicle.type && location.vehicle.type.description, ref: location.markerRef, zIndex: zIndex, position: { lat: location.latitude, lng: location.longitude } }, location.id));
    });
    const userHasVehicleListPermission = userHasPermission(loggedInUser, ['admin:vehicle:list']);
    return (_jsxs("div", { className: classnames('map-content', { 'map-search-hide': (hideSearch || !userHasVehicleListPermission) }), children: [userHasVehicleListPermission &&
                _jsx("div", { className: "map-show-search", children: _jsx(Button, { type: "primary", icon: _jsx(DoubleLeftOutlined, {}), onClick: toggleOpenCloseSearchPanel }) }), _jsx(FilterPanel, { loggedInUser: loggedInUser, selectedTenant: selectedTenant, driverLocations: filteredEmployeeDriverLocations, selectedDriverLocation: selectedDriverLocation, showSpinner: fetchingEmployees || fetchingConsignments || fetchingContractors || fetchingVehicleHires, onClose: toggleOpenCloseSearchPanel, onDriverClicked: onSearchPanelDriverClicked, searchText: searchText, onSearch: onSearch, onRefreshClicked: fetch, filterEmployees: sessionIncludeEmployees, onFilterEmployeesChanged: setSessionIncludeEmployees, filterContractors: sessionIncludeContractors, onFilterContractorsChanged: setSessionIncludeContractors, filterCarriers: sessionIncludeCarriers, onFilterCarriersChanged: setSessionIncludeCarriers, filterIncludeMarketplace: includeMarketplaceJobs, onFilterIncludeMarketplaceChanged: inc => {
                    setIncludeMarketplaceJobs(inc);
                    setSessionIncludeMarketplace(inc);
                }, filterVehicleCategories: vehicleTypeCategories, onFilterVehicleCategoriesChanged: onFilterVehicleCategoriesChanged }), _jsx(ConsignmentAssignVehicleModal, { open: showAssignToVehicleModal, consignment: selectedConsignment, onCancel: hideAssignToVehicleModal, onAssigned: () => {
                    hideAssignToVehicleModal();
                    clearSelectedConsignment();
                    fetch();
                } }), _jsx(VehicleHireAllocateVehicleModal, { open: showAllocateToVehicleModal, visibleTabs: [VehicleHireAllocateTab.FLEET], vehicleHireTenantId: selectedVehicleHire?.tenant.id, vehicleHireId: selectedVehicleHire?.id, vehicleHireVersion: selectedVehicleHire?.version, onCancel: hideAllocateToVehicleModal, onAllocated: () => {
                    hideAllocateToVehicleModal();
                    clearSelectedVehicleHire();
                    fetch();
                } }), _jsx("div", { className: "map-container", children: _jsxs("div", { className: "map-body", children: [_jsx("div", { id: "map-option-buttons", children: _jsx("button", { className: classnames('btn', { "on": showTraffic, "off": !showTraffic }), onClick: () => setShowTraffic(!showTraffic), children: _jsx(FontAwesomeIcon, { icon: faRoad }) }) }), _jsxs(GoogleMap, { initialCenter: CENTRE_OF_AUSTRALIA, centerAroundCurrentLocation: centerAroundCurrentLocation, boundMapToMarkers: boundMapToMarkers, showTraffic: showTraffic, onClick: onMapClicked, onCenterChanged: setMapCenter, onCurrentLocationFailed: onCurrentLocationFailed, children: [driverLocationMarkers, _jsx(MarkerCluster, { markerProps: marketplaceMarkers, onClusterClicked: onClusterClicked }), _jsx(DriverLocationInfoWindow, { driverLocation: selectedDriverLocation, showVehicleHireSummary: true, showConsignmentSummary: true, markerRef: selectedDriverLocation?.markerRef, visible: !!selectedDriverLocation, onClose: clearDriverSelection }), _jsx(MarketplaceConsignmentInfoWindow, { consignment: selectedConsignment, position: selectedConsignmentMarkerPosition, marker: selectedConsignmentMarker, visible: !!selectedConsignment, onClose: clearSelectedConsignment, onAssignClicked: () => setShowAssignToVehicleModal(true) }), _jsx(MarketplaceVehicleHireInfoWindow, { vehicleHire: selectedVehicleHire, position: selectedVehicleHireMarkerPosition, marker: selectedVehicleHireMarker, visible: !!selectedVehicleHire, onClose: clearSelectedVehicleHire, onAllocateClicked: () => setShowAllocateToVehicleModal(true) }), _jsx(MarketplaceClusterInfoWindow, { markers: selectedMarketplaceClusterMarkers, position: selectedMarketplaceClusterPosition, visible: selectedMarketplaceClusterMarkers.length > 0, onClose: clearSelectedMarketplaceCluster, onConsignmentMarkerClick: onClusteredConsignmentMarkerClick, onVehicleHireMarkerClick: onClusteredVehicleHireMarkerClick })] }, selectedTenant?.id)] }) })] }));
};
export default DriverMap;
